import { useEffect, useState, useRef } from "react";
import { fetchWithAuth } from "../../../../../api-client/api_client";
import { showErrorToast } from "../../../../shared/Toasts";
import {
  AutomatedTestSuiteExecution,
  PollAutomatedTestSuiteExecutionResponse,
  TestExecutionStatus,
} from "../types";

interface Params {
  integrationID: string;
  integrationVersionId: string | null;
  hasTests: boolean;
  pollingInterval?: number;
  onTestsInProgressChange?: (inProgress: boolean) => void;
}

const usePollAutomatedTestSuiteExecution = ({
  integrationID,
  integrationVersionId,
  hasTests,
  pollingInterval = 10000,
  onTestsInProgressChange,
}: Params) => {
  // state
  const [
    automatedTestSuiteExecution,
    setAutomatedTestSuiteExecution,
  ] = useState<AutomatedTestSuiteExecution | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [areTestsInProgress, setAreTestsInProgress] = useState(false);

  // derived state
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchData = () => {
    // Don't fetch if we don't have the required IDs or if there are no tests
    if (!integrationID || !integrationVersionId || !hasTests) {
      setIsInitialLoad(false);
      return;
    }

    fetchWithAuth({
      path: `/integrations/${integrationID}/${integrationVersionId}/poll-test-suite-execution`,
      method: "GET",
      body: {},
      expectJSON: true,
      onResponse: (data: PollAutomatedTestSuiteExecutionResponse) => {
        setAutomatedTestSuiteExecution(data.execution);
        setIsInitialLoad(false);

        // Check if any test executions are in PENDING or IN_PROGRESS state
        const hasInProgressTests =
          data.execution &&
          Object.values(data.execution.tests).some(
            (test) =>
              test.status === TestExecutionStatus.PENDING ||
              test.status === TestExecutionStatus.IN_PROGRESS
          );

        // Update in-progress state and notify callback
        const testsInProgress = !!hasInProgressTests;
        setAreTestsInProgress(testsInProgress);
        if (onTestsInProgressChange) {
          onTestsInProgressChange(testsInProgress);
        }

        // Start or stop polling based on test execution states
        if (hasInProgressTests) {
          // Only start polling if we don't have an active interval
          if (!intervalRef.current) {
            intervalRef.current = setInterval(fetchData, pollingInterval);
          }
        } else {
          // If no tests are in progress, clear the interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      },
      onError: (error) => {
        setIsInitialLoad(false);
        error
          ?.json()
          .then((value: string | Record<string, any>) => {
            let errorMessage = "";
            if (typeof value == "string") {
              errorMessage = value;
            } else {
              errorMessage = value?.non_field_errors ?? JSON.stringify(value);
            }
            showErrorToast(
              `Failed to fetch automated test suite execution. Error message: \n ${errorMessage}`
            );
          })
          .catch(() => {
            showErrorToast("Failed to fetch automated test suite execution");
          });
      },
    });
  };

  // effects
  // fetch data on mount and when dependencies change, cleanup interval on unmount
  useEffect(() => {
    if (integrationID && integrationVersionId && hasTests) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [integrationID, integrationVersionId, hasTests, pollingInterval]);

  return {
    automatedTestSuiteExecution,
    setAutomatedTestSuiteExecution,
    isInitialLoad,
    fetchData,
    areTestsInProgress,
  };
};

export default usePollAutomatedTestSuiteExecution;
